import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Typography, CircularProgress, Button, Card, CardContent, Divider } from '@mui/material';
import { toast } from 'react-toastify';
import { CheckCircleOutline, ErrorOutline } from '@mui/icons-material';
import COLORS from '../../constants/colors';
import baseURL from '../pages/user/config';
import Footer from '../pages/user/Reusbale/Footer';

const PayPalSuccess = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState(null);
    const [data, setData] = useState(null);
    const toastShown = useRef(false);

    const searchParams = new URLSearchParams(location.search);
    const token = searchParams.get('token'); // PayPal Order ID
    const payerId = searchParams.get('PayerID');

    useEffect(() => {
        if (!token || !payerId) {
            toast.error('Missing PayPal transaction parameters.');
            setStatus('failed');
            setLoading(false);
            return;
        }

        const verifyPayment = async () => {
            try {
                const response = await fetch(`${baseURL}/paypal/verify`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ token, payerId }),
                });
                const result = await response.json();

                setStatus(result.status);
                if (result.status === 'success') {
                    setData(result.data);
                    if (!toastShown.current) {
                        toast.success(result.message || 'Payment verified successfully.');
                        toastShown.current = true;
                    }
                } else {
                    if (!toastShown.current) {
                        toast.error(result.message || 'Payment verification failed.');
                        toastShown.current = true;
                    }
                }
            } catch (error) {
                console.error('Error verifying PayPal payment:', error);
                setStatus('failed');
                if (!toastShown.current) {
                    toast.error('An error occurred while verifying payment.');
                    toastShown.current = true;
                }
            } finally {
                setLoading(false);
            }
        };

        verifyPayment();
    }, [token, payerId]);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" flexDirection="column">
                <CircularProgress color="primary" size={24} />
                <Typography variant="h6" sx={{ mt: 2, color: COLORS.textSecondary }}>
                    Verifying your PayPal payment...
                </Typography>
                <Typography variant="body2" sx={{ mt: 1, color: COLORS.textSecondary }}>
                    Please do not close this window.
                </Typography>
            </Box>
        );
    }

    return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" px={2} flexDirection="column">
            <Card sx={{ p: 4, borderRadius: '20px', maxWidth: 500, width: '100%', boxShadow: 0 }}>
                <CardContent sx={{ textAlign: 'center' }}>
                    {status === 'success' ? (
                        <>
                            <CheckCircleOutline sx={{ fontSize: 64, color: 'green', mb: 2 }} />
                            <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                                PayPal Payment Successful!
                            </Typography>
                            {data?.name && (
                                <Typography variant="subtitle1" sx={{ mb: 1, color: COLORS.textSecondary }}>
                                    Thank you, <strong>{data.name}</strong>, for your generous donation
                                </Typography>
                            )}
                            {data?.campaignName && (
                                <Typography variant="body1" sx={{ mb: 2 }}>
                                    Donation for: <strong>{data.campaignName}</strong>
                                </Typography>
                            )}
                            <Divider sx={{ my: 2 }} />
                            <Typography variant="body1" sx={{ color: COLORS.textSecondary }}>
                                Transaction ID: <strong>{data?.captureId}</strong>
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 2, color: COLORS.textSecondary }}>
                                Amount: <strong>${data?.amount?.value}</strong>
                            </Typography>
                        </>
                    ) : (
                        <>
                            <ErrorOutline sx={{ fontSize: 64, color: 'red', mb: 2 }} />
                            <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1, color: 'red' }}>
                                PayPal Payment Failed
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 2, color: COLORS.textSecondary }}>
                                Something went wrong. Please try again or contact support.
                            </Typography>
                        </>
                    )}
                    <Button
                        variant="contained"
                        sx={{
                            backgroundColor: COLORS.accent,
                            color: COLORS.white,
                            borderRadius: '25px',
                            px: 3,
                            py: 1,
                            boxShadow: 0,
                            fontSize: '1rem',
                            fontWeight: 'bold',
                            textTransform: 'none',
                            '&:hover': { boxShadow: 0, backgroundColor: COLORS.accentDark },
                            mt: 1,
                            flexShrink: 0,
                        }}
                        onClick={() => navigate('/')}
                    >
                        Go to Home
                    </Button>
                </CardContent>
            </Card>
            <Footer />
        </Box>
    );
};

export default PayPalSuccess;
